/** XeAgents.ai**/
import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useGooglePlacesAutocomplete } from "@/hooks/useGooglePlacesAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Address {
  sid: string;
  customerName: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  isoCountry: string;
}

interface TwilioCountry {
  id: string;
  code: string;
  name: string;
  dialCode: string;
  isActive: boolean;
  sortOrder: number;
}

interface AddressManagementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCountry?: string;
}

export function AddressManagement({ open, onOpenChange, defaultCountry = "US" }: AddressManagementProps) {
  const { toast } = useToast();
  const streetInputRef = useRef<HTMLInputElement>(null);
  
  const [newAddress, setNewAddress] = useState({
    customerName: "",
    street: "",
    city: "",
    region: "",
    postalCode: "",
    isoCountry: defaultCountry,
  });

  // Google Places Autocomplete
  const { isLoaded, loadError } = useGooglePlacesAutocomplete(
    streetInputRef,
    (addressComponents) => {
      setNewAddress((prev) => ({
        ...prev,
        street: addressComponents.street,
        city: addressComponents.city,
        region: addressComponents.region,
        postalCode: addressComponents.postalCode,
        isoCountry: addressComponents.country || prev.isoCountry,
      }));
    }
  );

  // Fetch addresses
  const { data: addresses = [], isLoading: addressesLoading } = useQuery<Address[]>({
    queryKey: ["/api/addresses"],
    enabled: open,
  });

  // Fetch countries
  const { data: countries = [] } = useQuery<TwilioCountry[]>({
    queryKey: ["/api/twilio-countries"],
  });

  // Create address mutation
  const createAddressMutation = useMutation({
    mutationFn: async (addressData: Omit<Address, "sid">) => {
      const res = await apiRequest("POST", "/api/addresses", addressData);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/addresses"] });
      setNewAddress({
        customerName: "",
        street: "",
        city: "",
        region: "",
        postalCode: "",
        isoCountry: defaultCountry,
      });
      toast({
        title: "Address created",
        description: "Your address has been registered successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create address",
        description: error.message || "Please check your address details and try again.",
        variant: "destructive",
      });
    },
  });

  // Delete address mutation
  const deleteAddressMutation = useMutation({
    mutationFn: async (sid: string) => {
      const res = await apiRequest("DELETE", `/api/addresses/${sid}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/addresses"] });
      toast({
        title: "Address deleted",
        description: "The address has been removed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete address",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateAddress = () => {
    createAddressMutation.mutate(newAddress);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Addresses</DialogTitle>
          <DialogDescription>
            Create and manage addresses for regulatory compliance when purchasing phone numbers in certain countries.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Addresses */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Registered Addresses</Label>
            {addressesLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading addresses...
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <p className="text-muted-foreground">No addresses registered yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create an address below to purchase numbers in countries with regulatory requirements.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {addresses.map((addr) => (
                  <div
                    key={addr.sid}
                    className="flex items-start justify-between p-3 border rounded-lg hover:bg-accent/50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{addr.customerName}</div>
                      <div className="text-sm text-muted-foreground">
                        {addr.street}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {addr.city}, {addr.region} {addr.postalCode}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {addr.isoCountry}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAddressMutation.mutate(addr.sid)}
                      disabled={deleteAddressMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create New Address Form */}
          <div className="space-y-4 pt-4 border-t">
            <Label className="text-base font-semibold">Create New Address</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="addr-name">Customer Name *</Label>
                <Input
                  id="addr-name"
                  placeholder="John Smith"
                  value={newAddress.customerName}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, customerName: e.target.value })
                  }
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="addr-street">Street Address *</Label>
                <div className="relative">
                  <Input
                    ref={streetInputRef}
                    id="addr-street"
                    placeholder={
                      isLoaded
                        ? "Start typing to search address..."
                        : "123 Main Street"
                    }
                    value={newAddress.street}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, street: e.target.value })
                    }
                  />
                  {isLoaded && (
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                {isLoaded && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    ✓ Smart address search enabled - start typing to see suggestions
                  </p>
                )}
                {loadError && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠ {loadError.message === "Google API key not configured" 
                      ? "Smart search unavailable (Google API key not configured). You can still enter addresses manually."
                      : "Address search unavailable - you can still enter manually"}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="addr-city">City *</Label>
                <Input
                  id="addr-city"
                  placeholder="Sydney"
                  value={newAddress.city}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, city: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="addr-region">State/Region</Label>
                <Input
                  id="addr-region"
                  placeholder="NSW (for AU), CA (for US)"
                  value={newAddress.region}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, region: e.target.value.toUpperCase() })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Optional for UK, required for most other countries
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="addr-postal">Postal Code *</Label>
                <Input
                  id="addr-postal"
                  placeholder="2000 (AU), 94102 (US), SW1A 1AA (UK)"
                  value={newAddress.postalCode}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, postalCode: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="addr-country">Country *</Label>
                <Select
                  value={newAddress.isoCountry}
                  onValueChange={(value) =>
                    setNewAddress({ ...newAddress, isoCountry: value })
                  }
                >
                  <SelectTrigger id="addr-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name} ({country.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex gap-2">
                <div className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5">
                  ℹ️
                </div>
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">Address Format Requirements:</p>
                  <ul className="text-xs space-y-0.5 text-blue-800 dark:text-blue-200">
                    <li>• <strong>Australia (AU):</strong> Postcode must be 4 digits (e.g., 2000)</li>
                    <li>• <strong>UK (GB):</strong> Postcode format like SW1A 1AA, region optional</li>
                    <li>• <strong>US:</strong> ZIP code 5 digits or 5+4 (e.g., 94102)</li>
                    <li>• <strong>Canada (CA):</strong> Postal code format A1A 1A1</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCreateAddress}
              disabled={
                !newAddress.customerName ||
                !newAddress.street ||
                !newAddress.city ||
                !newAddress.postalCode ||
                !newAddress.isoCountry ||
                createAddressMutation.isPending
              }
              className="w-full"
            >
              {createAddressMutation.isPending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Creating Address...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Address
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export selector component for use in phone number purchase with smart search
export function AddressSelector({ 
  value, 
  onValueChange,
  onManageClick,
  required = false,
  defaultCountry = "US"
}: { 
  value: string;
  onValueChange: (value: string) => void;
  onManageClick: () => void;
  required?: boolean;
  defaultCountry?: string;
}) {
  const { toast } = useToast();
  const smartSearchInputRef = useRef<HTMLInputElement>(null);
  const [showSmartSearch, setShowSmartSearch] = useState(false);
  const [isCreatingAddress, setIsCreatingAddress] = useState(false);
  
  const [quickAddress, setQuickAddress] = useState({
    customerName: "",
    street: "",
    city: "",
    region: "",
    postalCode: "",
    isoCountry: defaultCountry,
  });

  const { data: addresses = [] } = useQuery<Address[]>({
    queryKey: ["/api/addresses"],
  });

  // Google Places Autocomplete for quick address
  const { isLoaded, loadError } = useGooglePlacesAutocomplete(
    smartSearchInputRef,
    (addressComponents) => {
      setQuickAddress((prev) => ({
        ...prev,
        street: addressComponents.street,
        city: addressComponents.city,
        region: addressComponents.region,
        postalCode: addressComponents.postalCode,
        isoCountry: addressComponents.country || prev.isoCountry,
      }));
    }
  );

  // Create address mutation for quick add
  const createQuickAddressMutation = useMutation({
    mutationFn: async (addressData: Omit<Address, "sid">) => {
      const res = await apiRequest("POST", "/api/addresses", addressData);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/addresses"] });
      onValueChange(data.sid); // Auto-select the newly created address
      setShowSmartSearch(false);
      setQuickAddress({
        customerName: "",
        street: "",
        city: "",
        region: "",
        postalCode: "",
        isoCountry: defaultCountry,
      });
      setIsCreatingAddress(false);
      toast({
        title: "Address created",
        description: "Address has been added and selected.",
      });
    },
    onError: (error: any) => {
      setIsCreatingAddress(false);
      toast({
        title: "Failed to create address",
        description: error.message || "Please check your address details.",
        variant: "destructive",
      });
    },
  });

  const handleQuickCreate = () => {
    if (!quickAddress.customerName || !quickAddress.street || !quickAddress.city || !quickAddress.postalCode) {
      toast({
        title: "Incomplete address",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    setIsCreatingAddress(true);
    createQuickAddressMutation.mutate(quickAddress);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>
          Address {required ? <span className="text-red-500">*</span> : "(Optional)"}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onManageClick}
          className="h-auto p-0 text-xs"
        >
          Manage Addresses
        </Button>
      </div>

      {!showSmartSearch ? (
        <>
          <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger className={required && !value ? "border-red-500" : ""}>
              <SelectValue 
                placeholder={
                  required 
                    ? "Select an address (required)" 
                    : "Select an address (optional)"
                } 
              />
            </SelectTrigger>
            <SelectContent>
              {addresses.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground text-center">
                  No addresses available. Use quick add below or manage addresses.
                </div>
              ) : (
                addresses.map((addr) => (
                  <SelectItem key={addr.sid} value={addr.sid}>
                    {addr.street}, {addr.city}, {addr.region} {addr.postalCode} ({addr.isoCountry})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowSmartSearch(true)}
            className="w-full"
          >
            <Plus className="h-3 w-3 mr-2" />
            Quick Add New Address
          </Button>
        </>
      ) : (
        <div className="border rounded-lg p-3 space-y-3 bg-accent/20">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Quick Add Address</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowSmartSearch(false);
                setQuickAddress({
                  customerName: "",
                  street: "",
                  city: "",
                  region: "",
                  postalCode: "",
                  isoCountry: defaultCountry,
                });
              }}
              className="h-auto p-1 text-xs"
            >
              Cancel
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quick-name" className="text-xs">Customer Name *</Label>
            <Input
              id="quick-name"
              placeholder="John Smith"
              value={quickAddress.customerName}
              onChange={(e) => setQuickAddress({ ...quickAddress, customerName: e.target.value })}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quick-street" className="text-xs">Street Address *</Label>
            <div className="relative">
              <Input
                ref={smartSearchInputRef}
                id="quick-street"
                placeholder={isLoaded ? "Start typing to search..." : "123 Main Street"}
                value={quickAddress.street}
                onChange={(e) => setQuickAddress({ ...quickAddress, street: e.target.value })}
                className="text-sm h-9"
              />
              {isLoaded && (
                <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              )}
            </div>
            {isLoaded && (
              <p className="text-xs text-green-600 dark:text-green-400">
                ✓ Smart search enabled
              </p>
            )}
            {loadError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                ⚠ Manual entry only
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="quick-city" className="text-xs">City *</Label>
              <Input
                id="quick-city"
                placeholder="Sydney"
                value={quickAddress.city}
                onChange={(e) => setQuickAddress({ ...quickAddress, city: e.target.value })}
                className="text-sm h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="quick-postal" className="text-xs">Postal *</Label>
              <Input
                id="quick-postal"
                placeholder="2000"
                value={quickAddress.postalCode}
                onChange={(e) => setQuickAddress({ ...quickAddress, postalCode: e.target.value })}
                className="text-sm h-9"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="quick-region" className="text-xs">Region (optional)</Label>
            <Input
              id="quick-region"
              placeholder="NSW"
              value={quickAddress.region}
              onChange={(e) => setQuickAddress({ ...quickAddress, region: e.target.value.toUpperCase() })}
              className="text-sm h-9"
              maxLength={3}
            />
          </div>

          <Button
            type="button"
            onClick={handleQuickCreate}
            disabled={
              !quickAddress.customerName ||
              !quickAddress.street ||
              !quickAddress.city ||
              !quickAddress.postalCode ||
              isCreatingAddress
            }
            className="w-full h-9"
            size="sm"
          >
            {isCreatingAddress ? (
              <>
                <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-3 w-3 mr-2" />
                Create & Select Address
              </>
            )}
          </Button>
        </div>
      )}

      {required ? (
        <p className="text-xs text-red-600 dark:text-red-400">
          * Required for regulatory compliance in selected country
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Some countries require a registered address for regulatory compliance.
        </p>
      )}
    </div>
  );
}
